import { useState, useId } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import { type SelectChangeEvent, Box, Popover, styled, IconButton, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import useTranslation, { type TextKeys } from 'canvas_states/translation/useTranslation';
import type Language from '../../../../types/Language';
import type Currency from '../../../../types/payment/Currency';
import useFormatCurrency from '../../utils/currency/useFormatCurrency';
import { isMobileScreen } from '../../utils/screenSizes/screenSizes';
import Typography from '../../atoms/Typography';
import ButtonWithEndIcon from '../../atoms/ButtonWithEndIcon/ButtonWithEndIcon';
import LanguageCurrencySelectors from '../LanguageCurrencySelectors';
import { GoogleAnalyticClick } from '../../../../types/analytics/GoogleAnalyticsEvent';
import useCommmonEvents from '../../utils/analytics/useCommonEvents';

export const makeDataTestId = (dataTestId = 'languageCurrencySettings') => {
  return {
    languageCurrencySettingsButton: `${dataTestId}Button`,
    languageCurrencySettingsButtonIcon: `${dataTestId}ButtonIcon`,
    settingsPopoverCloseButton: `${dataTestId}PopoverCloseButton`,
    languageCurrencySettingsContainer: `${dataTestId}Container`,
  };
};

const allDataTestId = makeDataTestId();

const StyledPopover = styled(Popover)`
  & .MuiPopover-paper {
    margin-top: 1.6rem;
    overflow: visible;
    border-radius: 16px;
  }
`;

const StyledPopoverPadding = styled(Box)`
  box-sizing: border-box;
  padding: 0.8rem 1.5rem 1.7rem 1.5rem;
  @media (max-width: 25rem) {
    width: 90vw;
  }
  @media (min-width: 25rem) {
    min-width: 19rem;
  }
`;

const StyledButton = styled(ButtonWithEndIcon)(
  ({ theme }) => `
  background-color: ${theme.palette.background.paper};
  color: ${theme.palette.primary.main};
`,
);

const InnerButton = styled(Typography)`
  text-transform: none;
`;

const StyledSeparator = styled('span')`
  display: inline-block;
  padding: 0rem 0.5rem;
`;

export interface LanguageCurrencySettingsProps {
  handleLanguageChange: (event: SelectChangeEvent) => void;
  handleCurrencyChange: (event: SelectChangeEvent) => void;
  hideCurrencySelector?: boolean; // if the user already has items in the card it can't change the currency anymore
  disableCurrencySelector?: boolean;
  hideLanguagesSelector?: boolean;
  languages?: Language[];
  currencies?: Currency[];
  currentLanguage?: Language;
  currentCurrency?: Currency;
}

const LanguageCurrencySettings: React.FC<LanguageCurrencySettingsProps> = ({
  handleLanguageChange,
  handleCurrencyChange,
  hideCurrencySelector,
  disableCurrencySelector,
  hideLanguagesSelector,
  languages = [],
  currencies = [],
  currentLanguage = { code: '', label: '' },
  currentCurrency = { code: '', label: '', symbol: '' },
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { t } = useTranslation().actions;
  const eventPublisher = useCommmonEvents();

  const handleGlobalIconClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    eventPublisher.actions.publishClick(GoogleAnalyticClick.CHANGE_LANGUAGE_DROPDOWN);
  };

  const handlePopupClose = () => setAnchorEl(null);

  const settingsId = useId();

  const { formatCurrency } = useFormatCurrency().actions;

  const theme = useTheme();
  const isMobile = useMediaQuery(isMobileScreen(theme));
  const headerLanguage = t('header.language');
  return (
    <>
      {isMobile ? (
        <IconButton
          color="inherit"
          onClick={handleGlobalIconClick}
          aria-label={headerLanguage}
          data-testid={allDataTestId.languageCurrencySettingsButton}
        >
          <LanguageOutlinedIcon data-testid={allDataTestId.languageCurrencySettingsButtonIcon} />
        </IconButton>
      ) : (
        <StyledButton
          variant="contained"
          data-testid={allDataTestId.languageCurrencySettingsButton}
          aria-label={headerLanguage}
          onClick={handleGlobalIconClick}
          endIcon={<ExpandMoreIcon />}
          disableElevation
          color="inherit"
        >
          <InnerButton variant="body2">
            {!hideLanguagesSelector && t(currentLanguage.label as keyof TextKeys)}
            {!hideCurrencySelector && !hideLanguagesSelector && <StyledSeparator>|</StyledSeparator>}
            {!hideCurrencySelector && formatCurrency(currentCurrency)}
          </InnerButton>
        </StyledButton>
      )}

      <StyledPopover
        id={settingsId}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopupClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        elevation={3}
      >
        <StyledPopoverPadding data-testid={allDataTestId.languageCurrencySettingsContainer}>
          <LanguageCurrencySelectors
            currentLanguage={currentLanguage}
            handleCurrencyChange={(e: SelectChangeEvent) => {
              handleCurrencyChange(e);
              handlePopupClose();
            }}
            handleLanguageChange={(e: SelectChangeEvent) => {
              handleLanguageChange(e);
              handlePopupClose();
            }}
            hideCurrencySelector={hideCurrencySelector}
            disableCurrencySelector={disableCurrencySelector}
            hideLanguagesSelector={hideLanguagesSelector}
            languages={languages}
            currencies={currencies}
            currentCurrency={currentCurrency}
          />
        </StyledPopoverPadding>
      </StyledPopover>
    </>
  );
};

export default LanguageCurrencySettings;
